import { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';

/**
 * 15분 단위로 실시간 업데이트를 위한 커스텀 훅
 * @param {Function} onUpdate - 업데이트 콜백 함수
 * @param {Object} options - 옵션 설정
 * @returns {Object} - 현재 시간, 다음 업데이트 시간, 업데이트 함수 등
 */
const useRealtimeScheduler = (onUpdate, options = {}) => {
  const {
    interval = 15, // 15분 단위
    enabled = true,
    immediate = false // 즉시 실행 여부
  } = options;

  const [nextUpdateTime, setNextUpdateTime] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(() => {
    // 초기값을 현재 시간의 15분 단위로 설정
    const now = dayjs();
    const currentMinute = now.minute();
    
    let flooredMinute;
    if (currentMinute < 15) {
      flooredMinute = 0;
    } else if (currentMinute < 30) {
      flooredMinute = 15;
    } else if (currentMinute < 45) {
      flooredMinute = 30;
    } else {
      flooredMinute = 45;
    }
    
    return now.minute(flooredMinute).second(0).millisecond(0);
  });
  const [isUpdating, setIsUpdating] = useState(false);
  
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const onUpdateRef = useRef(onUpdate);
  const executeUpdateRef = useRef();
  
  // onUpdate 함수를 ref에 저장하여 안정화
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // 다음 15분 단위 시간 계산 (20초 지연)
  const getNextUpdateTime = useCallback(() => {
    const now = dayjs();
    const currentMinute = now.minute();
    
    // 15분 단위로 정확히 계산: 0, 15, 30, 45분
    let nextMinute;
    if (currentMinute < 15) {
      nextMinute = 15;
    } else if (currentMinute < 30) {
      nextMinute = 30;
    } else if (currentMinute < 45) {
      nextMinute = 45;
    } else {
      // 45분 이후면 다음 시간의 0분 (15분 단위)
      nextMinute = 0;
    }
    
    if (nextMinute === 0) {
      // 다음 시간으로 넘어가는 경우
      return now.add(1, 'hour').startOf('hour').add(20, 'second');
    } else {
      // 같은 시간 내에서 다음 15분 단위 + 20초 지연
      return now.minute(nextMinute).second(20).millisecond(0);
    }
  }, [interval]);

  // 현재 시간을 15분 단위로 내림하는 함수
  const getCurrentTimeRounded = useCallback(() => {
    const now = dayjs();
    const currentMinute = now.minute();
    
    // 15분 단위로 내림 (0, 15, 30, 45분)
    let flooredMinute;
    if (currentMinute < 15) {
      flooredMinute = 0;
    } else if (currentMinute < 30) {
      flooredMinute = 15;
    } else if (currentMinute < 45) {
      flooredMinute = 30;
    } else {
      flooredMinute = 45;
    }
    
    return now.minute(flooredMinute).second(0).millisecond(0);
  }, []);

  // 업데이트 실행 함수
  const executeUpdate = useCallback(async () => {
    setIsUpdating(prev => {
      if (prev) return prev; // 이미 업데이트 중이면 무시
      return true;
    });
    
    const updateTime = dayjs();
    const roundedUpdateTime = getCurrentTimeRounded();
    
    try {
      await onUpdateRef.current(roundedUpdateTime);
      setLastUpdateTime(roundedUpdateTime);
    } catch (error) {
      console.error('실시간 업데이트 실패:', error);
    } finally {
      setIsUpdating(false);
    }
  }, [getCurrentTimeRounded]);
  
  // executeUpdate를 ref에 저장
  useEffect(() => {
    executeUpdateRef.current = executeUpdate;
  }, [executeUpdate]);

  // 스케줄러 시작
  const startScheduler = useCallback(() => {
    if (!enabled) return;

    const nextTime = getNextUpdateTime();
    setNextUpdateTime(nextTime);
    
    const now = dayjs();
    const timeUntilNext = nextTime.diff(now);
    
    
    // 다음 업데이트까지 대기
    timeoutRef.current = setTimeout(() => {
      executeUpdateRef.current();
      
      // 이후 15분마다 반복
      intervalRef.current = setInterval(() => {
        executeUpdateRef.current();
      }, interval * 60 * 1000);
      
    }, timeUntilNext);
  }, [enabled, getNextUpdateTime, interval]);

  // 스케줄러 중지
  const stopScheduler = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // 수동 업데이트
  const manualUpdate = useCallback(() => {
    executeUpdateRef.current();
  }, []);

  // 컴포넌트 마운트 시 스케줄러 시작
  useEffect(() => {
    if (enabled) {
      if (immediate) {
        executeUpdateRef.current();
      }
      startScheduler();
    }

    return () => {
      stopScheduler();
    };
  }, [enabled, immediate]); // startScheduler, stopScheduler 제거

  // 현재 시간은 필요할 때만 계산 (리렌더링 최적화)
  const getCurrentTime = useCallback(() => dayjs(), []);

  return {
    getCurrentTime,
    nextUpdateTime,
    lastUpdateTime,
    isUpdating,
    manualUpdate,
    startScheduler,
    stopScheduler
  };
};

export default useRealtimeScheduler;
